import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import { useForm, Controller } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { ToastContainer } from "react-toastify";
import {
  Box,
  Button,
  TextField,
  Typography,
  Grid,
  Select,
  MenuItem,
  InputLabel,
  FormControl,
  FormHelperText,
  useTheme,
  InputAdornment,
  Divider,
  Paper,
} from "@mui/material";
import {
  User,
  Clock,
  Briefcase,
  Users,
  CheckCircle,
  Tag,
  DollarSign,
  Plus
} from 'lucide-react';
import { useAddOns } from "../provider/AddOnsProvider";
import Constants from "../../utils/constants/Constants";
import HttpClient from "../../utils/functions/HttpClient";
import ServerEndpoints from "../../utils/constants/ServerEndpoints";
import { Services } from "../../utils/models/ServiceModel";
import AddonsList from "../addons/AddonsList";
import { createReservation } from "./ReservationsServices";
import {
  reservationSchema,
  ReservationModel,
  defaultDiscount,
} from "../../utils/models/ReservationModel";
import {
  computeSubtotal,
  formatDateTimeForInput,
  formatDateTimeForBackend,
  USDollar,
  generateInitialSubtotalBreakDown,
  timeOptions,
} from "../../utils/functions/Helpers";
import { useLoadingSpinner } from "../../utils/components/LoadingSpinnerProvider";
import { tokens } from "../../theme";

const defaultPricingRates = {
  seatRate: Constants.SEAT_RATE,
  overtimeHourlyRate: Constants.OVERTIME_RATE,
  regularFacilityRate: Constants.REGULAR_DAYS_RATE,
  saturdayFacilityRate: Constants.SATURDAY_RATE,
  hourlyFacilityRate: Constants.HOURLY_FACILITY_RATE,
  cleaningFeesSmallGuestsCount: Constants.CLEANING_FEES_SMALL_GUESTS_COUNT,
  cleaningFeesLargeGuestsCount: Constants.CLEANING_FEES_LARGE_GUESTS_COUNT,
  taxRate: Constants.TAX_RATE,
};

const pricingNameToKeyMap = {
  [Constants.SEAT_RATE_NAME]: "seatRate",
  [Constants.OVERTIME_HOURLY_RATE_NAME]: "overtimeHourlyRate",
  [Constants.REGULAR_FACILITY_RATE_NAME]: "regularFacilityRate",
  [Constants.SATURDAY_FACILITY_RATE_NAME]: "saturdayFacilityRate",
  [Constants.HOURLY_FACILITY_RATE_NAME]: "hourlyFacilityRate",
  [Constants.CLEANING_FEES_SMALL_GUESTS_COUNT_NAME]: "cleaningFeesSmallGuestsCount",
  [Constants.CLEANING_FEES_LARGE_GUESTS_COUNT_NAME]: "cleaningFeesLargeGuestsCount",
  [Constants.TAX_RATE_NAME]: "taxRate",
};

const mapPricingRowsToRates = (pricingRows = []) =>
  pricingRows.reduce((acc, pricingRow) => {
    const key = pricingNameToKeyMap[pricingRow?.name];

    if (!key) {
      return acc;
    }

    const numericPrice = Number(pricingRow?.price ?? 0);
    acc[key] = key === "taxRate" ? numericPrice / 100 : numericPrice;
    return acc;
  }, { ...defaultPricingRates });

const buildReservationFormValues = (reservation) => ({
  id: reservation?.id ?? null,
  startingDateTime: reservation?.startingDateTime
    ? formatDateTimeForInput(reservation.startingDateTime)
    : "",
  endingDateTime: reservation?.endingDateTime
    ? formatDateTimeForInput(reservation.endingDateTime)
    : "",
  effectiveEndingDateTime: reservation?.effectiveEndingDateTime
    ? formatDateTimeForInput(reservation.effectiveEndingDateTime)
    : "",
  eventType: reservation?.eventType ?? "",
  numberOfSeats: reservation?.numberOfSeats ?? "",
  addOns: reservation?.addOns ?? [],
  addOnsTotalCost: reservation?.addOnsTotalCost ?? 0,
  status: reservation?.status ?? Constants.STATUS.Pending,
  fullPackage: reservation?.fullPackage ?? false,
  securityDepositRefunded: reservation?.securityDepositRefunded ?? false,
  taxRate: reservation?.taxRate ?? Constants.TAX_RATE,
  totalPrice: reservation?.totalPrice ?? 0,
  discount: reservation?.discount ?? defaultDiscount,
  pricingStrategy: reservation?.pricingStrategy ?? "FULL_DAY",
  userId: reservation?.userId ?? "",
});

const ReservationForm = (props) => {
  const {
    reservation,
    updateReservation,
    usersData,
    toggleModal,
    setState,
    isEditing = false,
  } = props;

  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const { addOnsData, setParams } = useAddOns();
  const setIsLoading = useLoadingSpinner();
  const [, setApiError] = useState(false);

  const status_options = Object.values(Constants.STATUS);

  const {
    clearErrors,
    control,
    getValues,
    handleSubmit,
    register,
    reset,
    setValue,
    formState: { errors },
    watch,
  } = useForm({
    resolver: yupResolver(reservationSchema),
    defaultValues: buildReservationFormValues(reservation),
  });

  const discountType = watch("discount.type");

  const now = new Date().toLocaleString("en-US", timeOptions);
  const todayAt12AM = new Date(now).setHours(0, 0, 0, 0);
  const nextDayAt12AM = new Date(todayAt12AM).setHours(20, 0, 0, 0);

  const minDateTime =
    new Date(now) > new Date(now).setHours(19, 30, 0, 0)
      ? new Date(nextDayAt12AM).toISOString().slice(0, 16)
      : new Date(todayAt12AM).toISOString().slice(0, 16);

  const initialSubBreakDown = generateInitialSubtotalBreakDown(reservation);

  const [subTotal, setSubTotal] = useState(
    reservation ? reservation.totalPrice / (1 + reservation.taxRate) : 0
  );
  const [taxe, setTaxe] = useState(
    reservation ? reservation.totalPrice - subTotal : 0
  );
  const [searchQuery, setSearchQuery] = useState("");
  const [categoryFilters, setCategoryFilters] = useState([]);
  const [pricingRates, setPricingRates] = useState(defaultPricingRates);
  const [subTotalBreakDown, setSubTotalBreakDown] =
    useState(initialSubBreakDown);

  const primaryServices = Services.slice(0, -1)
    .filter((service) => service.available)
    .sort((a, b) => a.title.localeCompare(b.title));
  const otherService = Services.at(-1);

  const resetForm = () => {
    reset();
    setTaxe(0);
    setSubTotal(0);
    setSearchQuery("");
    setValue("addOns", []);
    setCategoryFilters([]);
    setSubTotalBreakDown(generateInitialSubtotalBreakDown(ReservationModel));

    clearErrors();
    toggleModal !== undefined && toggleModal(false);
  };

  const onSubmit = async (data) => {
    data.startingDateTime = formatDateTimeForBackend(data.startingDateTime);
    data.endingDateTime = formatDateTimeForBackend(data.endingDateTime);
    if (data.effectiveEndingDateTime) {
      data.effectiveEndingDateTime = formatDateTimeForBackend(
        data.effectiveEndingDateTime
      );
    } else {
      delete data.effectiveEndingDateTime;
    }

    if (!data.discount?.type?.trim?.()) {
      data.discount = null;
    }
    setIsLoading(true);
    setApiError(false);

    try {
      if (isEditing) {
        await updateReservation(data, setState, setApiError, setIsLoading);
      } else {
        await createReservation(data, setState, setApiError, setIsLoading);
      }
      resetForm();
    } catch (err) {
      console.error("Submission failed", err);
    }
  };

  const rates = pricingRates;

  const applyDiscount = (subtotal, discount) => {
    if (discount.type === Constants.DISCOUNT_TYPES.Amount) {
      return subtotal - discount.amount;
    }
    if (discount.type === Constants.DISCOUNT_TYPES.Percentage) {
      return subtotal - subtotal * (discount.percentage / 100);
    }
    return subtotal;
  };

  const applyTaxes = (subtotal, taxRate) => {
    return subtotal * taxRate;
  };

  const computeTotal = (subtotal, discount, taxRate) => {
    const subtotalAfterDiscount = applyDiscount(subtotal, discount);
    const taxe = applyTaxes(subtotalAfterDiscount, taxRate);
    setTaxe(taxe);
    return subtotalAfterDiscount + taxe;
  };

  // Wrapper to always pass pricingStrategy to computeSubtotal
  const computeSubtotalWithStrategy = (addOnsCost, seats, dateTimes, ratesObj, setBreakdown) => {
    const data = getValues();
    return computeSubtotal(addOnsCost, seats, dateTimes, ratesObj, setBreakdown, data.pricingStrategy);
  };

  const handlePricingStrategyChange = (e) => {
    const strategy = e.target.value;
    setValue("pricingStrategy", strategy);
    const data = getValues();
    const dateTimes = {
      startingDateTime: data.startingDateTime,
      endingDateTime: data.endingDateTime,
      effectiveEndingDateTime: data.effectiveEndingDateTime,
    };
    const subtotal = computeSubtotalWithStrategy(
      data.addOnsTotalCost,
      data.numberOfSeats,
      dateTimes,
      rates,
      setSubTotalBreakDown
    );
    setSubTotal(subtotal);
    setValue("totalPrice", computeTotal(subtotal, data.discount, data.taxRate));
  };

  const handleDiscountValueChange = (onChange) => (e) => {
    const discountValue = e.target.valueAsNumber;
    if (typeof discountValue === "number" && !isNaN(discountValue)) {
      onChange(discountValue);
    } else {
      onChange(0);
    }
    const data = getValues();
    setValue("totalPrice", computeTotal(subTotal, data.discount, data.taxRate));
  };

  const handleGuestCountChange = (e) => {
    let numberOfSeats = e.target.valueAsNumber;
    const data = getValues();
    if (typeof numberOfSeats === "number" && !isNaN(numberOfSeats)) {
      setValue("numberOfSeats", numberOfSeats);
    } else {
      setValue("numberOfSeats", "");
      numberOfSeats = 0;
    }
    const dateTimes = {
      startingDateTime: data.startingDateTime,
      endingDateTime: data.endingDateTime,
      effectiveEndingDateTime: data.effectiveEndingDateTime,
    };
    const subtotal = computeSubtotalWithStrategy(
      data.addOnsTotalCost,
      numberOfSeats,
      dateTimes,
      rates,
      setSubTotalBreakDown
    );
    setSubTotal(subtotal);

    setValue("totalPrice", computeTotal(subtotal, data.discount, data.taxRate));
  };

  const handleEndingDatetimeChange = (onChange) => (e) => {
    const endingDateTime = e.target.value;
    onChange(endingDateTime);
    const data = getValues();
    const dateTimes = {
      startingDateTime: data.startingDateTime,
      endingDateTime: endingDateTime,
      effectiveEndingDateTime: data.effectiveEndingDateTime,
    };
    const subtotal = computeSubtotalWithStrategy(
      data.addOnsTotalCost,
      data.numberOfSeats,
      dateTimes,
      rates,
      setSubTotalBreakDown
    );
    setSubTotal(subtotal);

    setValue("totalPrice", computeTotal(subtotal, data.discount, data.taxRate));
  };

  const handleEffectiveEndingDatetimeChange = (onChange) => (e) => {
    const effectiveEndingDateTime = e.target.value;
    onChange(effectiveEndingDateTime);
    const data = getValues();
    const dateTimes = {
      startingDateTime: data.startingDateTime,
      endingDateTime: data.endingDateTime,
      effectiveEndingDateTime: effectiveEndingDateTime,
    };
    const subtotal = computeSubtotalWithStrategy(
      data.addOnsTotalCost,
      data.numberOfSeats,
      dateTimes,
      rates,
      setSubTotalBreakDown
    );

    setSubTotal(subtotal);
    setValue("totalPrice", computeTotal(subtotal, data.discount, data.taxRate));
  };

  const handleStartingDateTimeChange = (onChange) => (e) => {
    const startingDateTime = e.target.value;
    onChange(startingDateTime);
    const data = getValues();
    const dateTimes = {
      startingDateTime: startingDateTime,
      endingDateTime: data.endingDateTime,
      effectiveEndingDateTime: data.effectiveEndingDateTime,
    };
    const subtotal = computeSubtotalWithStrategy(
      data.addOnsTotalCost,
      data.numberOfSeats,
      dateTimes,
      rates,
      setSubTotalBreakDown
    );
    setSubTotal(subtotal);

    setValue("totalPrice", computeTotal(subTotal, data.discount, data.taxRate));
  };

  const handleCategoryFilterChange = (option) => {
    const selectedCategory = option;
    if (categoryFilters.includes(selectedCategory)) {
      setCategoryFilters(
        categoryFilters.filter((category) => category !== selectedCategory)
      );
    } else {
      setCategoryFilters([...categoryFilters, selectedCategory]);
    }
  };

  const handleSearchQueryChange = (event) => {
    setSearchQuery(event.target.value);
  };

  const handleItemQuantityChange = (selectedItem, quantity) => {
    const data = getValues();
    const prevSelectedItems = data.addOns.slice();
    const existingItemIndex = prevSelectedItems.findIndex(
      (item) => item.addOn.name === selectedItem.name
    );

    if (quantity > 0) {
      if (existingItemIndex >= 0) {
        prevSelectedItems[existingItemIndex].quantity = quantity;
      }
      if (existingItemIndex === -1) {
        prevSelectedItems.push({ addOn: selectedItem, quantity });
      }
    } else {
      prevSelectedItems.splice(existingItemIndex, 1);
    }

    const addOnsTotalCost = prevSelectedItems.reduce(
      (acc, { addOn, quantity }) => acc + addOn.price * quantity,
      0
    );

    setValue("addOnsTotalCost", addOnsTotalCost);

    const dateTimes = {
      startingDateTime: data.startingDateTime,
      endingDateTime: data.endingDateTime,
      effectiveEndingDateTime: data.effectiveEndingDateTime,
    };

    const subtotal = computeSubtotalWithStrategy(
      addOnsTotalCost,
      data.numberOfSeats,
      dateTimes,
      rates,
      setSubTotalBreakDown
    );

    setSubTotal(subtotal);

    setValue("addOns", prevSelectedItems);
    setValue("totalPrice", computeTotal(subtotal, data.discount, data.taxRate));
  };

  useEffect(() => {
    setParams({ pageSize: "All", page: 0 });
  }, [setParams]);

  useEffect(() => {
    const nextFormValues = buildReservationFormValues(reservation);
    reset(nextFormValues);

    const reservationSubtotal = reservation?.totalPrice != null && reservation?.taxRate != null
      ? reservation.totalPrice / (1 + reservation.taxRate)
      : 0;

    setSubTotal(reservationSubtotal);
    setTaxe(reservation?.totalPrice != null ? reservation.totalPrice - reservationSubtotal : 0);
    setSearchQuery("");
    setCategoryFilters([]);
    setSubTotalBreakDown(generateInitialSubtotalBreakDown(reservation ?? ReservationModel));
  }, [reservation, reset]);

  useEffect(() => {
    const fetchPricingRates = async () => {
      try {
        const response = await HttpClient.getAxiosClient().get(
          `${ServerEndpoints.ADMIN_PRICING_ENDPOINT}`,
          { params: { pageSize: "All", page: 0 } }
        );

        const nextPricingRates = mapPricingRowsToRates(response?.data?.content ?? []);
        setPricingRates(nextPricingRates);

        if (reservation?.taxRate == null) {
          setValue("taxRate", nextPricingRates.taxRate);
        }
      } catch (error) {
        console.error("Failed to load pricing rates", error);
      }
    };

    fetchPricingRates();
  }, [reservation?.taxRate, setValue]);

  return (
    <Box
      sx={{
        bgcolor: theme.palette.mode === "dark" ? colors.primary[500] : "#FFFFFF",
        p: { xs: 2, md: 5 },
        borderRadius: "24px",
      }}
    >
      <form onSubmit={handleSubmit(onSubmit)}>
        <Box
          sx={{
            mb: 4,
            p: 3,
            borderRadius: "20px",
            background: `linear-gradient(135deg, ${colors.greenAccent[600]} 0%, ${colors.greenAccent[800]} 100%)`,
            color: "white",
            textAlign: 'center'
          }}
        >
          <Typography variant="h2" fontWeight="800" sx={{ fontFamily: "Josefin Sans" }}>
            Reservation Management
          </Typography>
          <Typography variant="h6" sx={{ opacity: 0.8 }}>
            {isEditing ? `Refining Reservation #${reservation?.id}` : "Crafting a New Event Experience"}
          </Typography>
        </Box>

        <Grid container spacing={4}>
          {/* Section 1: Basic Info */}
          <Grid item xs={12}>
            <Typography variant="h5" fontWeight="700" color={colors.greenAccent[500]} sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
              <Briefcase size={22} /> Event Basics
            </Typography>
            <Divider sx={{ mb: 3 }} />
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <FormControl fullWidth error={!!errors.userId}>
                  <InputLabel id="user-label" sx={{ color: colors.grey[300], "&.Mui-focused": { color: colors.greenAccent[500] } }}>Event Host</InputLabel>
                  <Controller
                    name="userId"
                    control={control}
                    render={({ field }) => (
                      <Select
                        labelId="user-label"
                        label="Event Host"
                        {...field}
                        sx={{
                          borderRadius: "12px",
                          color: colors.grey[100],
                          "& .MuiOutlinedInput-notchedOutline": { borderColor: colors.grey[400] },
                          "&.Mui-focused .MuiOutlinedInput-notchedOutline": { borderColor: colors.greenAccent[500] },
                          "& .MuiSvgIcon-root": { color: colors.grey[100] }
                        }}
                        startAdornment={<User size={18} style={{ marginRight: '8px', color: colors.grey[400] }} />}
                      >
                        <MenuItem value="" sx={{ color: colors.grey[100] }}>Select Host</MenuItem>
                        {usersData.map((user) => (
                          <MenuItem key={user.userId} value={user.userId} sx={{ color: colors.grey[100] }}>
                            {`${user.firstName} ${user.lastName}`}
                          </MenuItem>
                        ))}
                      </Select>
                    )}
                  />
                  <FormHelperText>{errors.userId?.message}</FormHelperText>
                </FormControl>
              </Grid>

              <Grid item xs={12} md={6}>
                <FormControl fullWidth error={!!errors.eventType}>
                  <InputLabel id="event-type-label" sx={{ color: colors.grey[300], "&.Mui-focused": { color: colors.greenAccent[500] } }}>Occasion Type</InputLabel>
                  <Controller
                    name="eventType"
                    control={control}
                    render={({ field }) => (
                      <Select
                        labelId="event-type-label"
                        label="Occasion Type"
                        {...field}
                        sx={{
                          borderRadius: "12px",
                          color: colors.grey[100],
                          "& .MuiOutlinedInput-notchedOutline": { borderColor: colors.grey[400] },
                          "&.Mui-focused .MuiOutlinedInput-notchedOutline": { borderColor: colors.greenAccent[500] },
                          "& .MuiSvgIcon-root": { color: colors.grey[100] }
                        }}
                        startAdornment={<Tag size={18} style={{ marginRight: '8px', color: colors.grey[400] }} />}
                      >
                        <MenuItem value="" sx={{ color: colors.grey[100] }}>Select Occasion</MenuItem>
                        {primaryServices.map((service) => (
                          <MenuItem key={service.id} value={service.title} sx={{ color: colors.grey[100] }}>
                            {service.title}
                          </MenuItem>
                        ))}
                        <MenuItem key={otherService.id} value={otherService.title} sx={{ color: colors.grey[100] }}>
                          {otherService.title}
                        </MenuItem>
                      </Select>
                    )}
                  />
                  <FormHelperText>{errors.eventType?.message}</FormHelperText>
                </FormControl>
              </Grid>

              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  type="number"
                  label="Guest Capacity"
                  {...register("numberOfSeats")}
                  onChange={handleGuestCountChange}
                  error={!!errors.numberOfSeats}
                  helperText={errors.numberOfSeats?.message}
                  variant="outlined"
                  sx={{ "& .MuiOutlinedInput-root": { borderRadius: "12px" } }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Users size={18} color={colors.grey[400]} />
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <FormControl fullWidth>
                  <InputLabel id="pricing-strategy-label" sx={{ color: colors.grey[300], "&.Mui-focused": { color: colors.greenAccent[500] } }}>Pricing Strategy</InputLabel>
                  <Controller
                    name="pricingStrategy"
                    control={control}
                    render={({ field }) => (
                      <Select
                        labelId="pricing-strategy-label"
                        label="Pricing Strategy"
                        {...field}
                        value={field.value || "FULL_DAY"}
                        onChange={(e) => {
                          field.onChange(e);
                          handlePricingStrategyChange(e);
                        }}
                        sx={{
                          borderRadius: "12px",
                          color: colors.grey[100],
                          "& .MuiOutlinedInput-notchedOutline": { borderColor: colors.grey[400] },
                          "&.Mui-focused .MuiOutlinedInput-notchedOutline": { borderColor: colors.greenAccent[500] },
                          "& .MuiSvgIcon-root": { color: colors.grey[100] }
                        }}
                        startAdornment={<DollarSign size={18} style={{ marginRight: '8px', color: colors.grey[400] }} />}
                      >
                        <MenuItem value="FULL_DAY" sx={{ color: colors.grey[100] }}>Full-Day Rate</MenuItem>
                        <MenuItem value="HOURLY" sx={{ color: colors.grey[100] }}>Hourly Rate</MenuItem>
                      </Select>
                    )}
                  />
                </FormControl>
              </Grid>

              <Grid item xs={12} md={6}>
                <FormControl fullWidth error={!!errors.status}>
                  <InputLabel id="status-label" sx={{ color: colors.grey[300], "&.Mui-focused": { color: colors.greenAccent[500] } }}>Reservation Status</InputLabel>
                  <Controller
                    name="status"
                    control={control}
                    render={({ field }) => (
                      <Select
                        labelId="status-label"
                        label="Reservation Status"
                        {...field}
                        sx={{
                          borderRadius: "12px",
                          color: colors.grey[100],
                          "& .MuiOutlinedInput-notchedOutline": { borderColor: colors.grey[400] },
                          "&.Mui-focused .MuiOutlinedInput-notchedOutline": { borderColor: colors.greenAccent[500] },
                          "& .MuiSvgIcon-root": { color: colors.grey[100] }
                        }}
                        startAdornment={<CheckCircle size={18} style={{ marginRight: '8px', color: colors.grey[400] }} />}
                      >
                        <MenuItem value="" sx={{ color: colors.grey[100] }}>Select Status</MenuItem>
                        {status_options.map((s) => (
                          <MenuItem key={s} value={s} sx={{ color: colors.grey[100] }}>{s}</MenuItem>
                        ))}
                      </Select>
                    )}
                  />
                  <FormHelperText>{errors.status?.message}</FormHelperText>
                </FormControl>
              </Grid>
            </Grid>
          </Grid>

          {/* Section 2: Timeline */}
          <Grid item xs={12}>
            <Typography variant="h5" fontWeight="700" color={colors.greenAccent[500]} sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
              <Clock size={22} /> Event Timeline
            </Typography>
            <Divider sx={{ mb: 3 }} />
            <Grid container spacing={3}>
              <Grid item xs={12} md={4}>
                <Controller
                  name="startingDateTime"
                  control={control}
                  render={({ field: { onChange, value } }) => (
                    <TextField
                      fullWidth
                      label="Commencement"
                      type="datetime-local"
                      value={value || ""}
                      min={minDateTime}
                      onChange={handleStartingDateTimeChange(onChange)}
                      error={!!errors.startingDateTime}
                      helperText={errors.startingDateTime?.message}
                      InputLabelProps={{ shrink: true }}
                      variant="outlined"
                      sx={{ "& .MuiOutlinedInput-root": { borderRadius: "12px" } }}
                    />
                  )}
                />
              </Grid>

              <Grid item xs={12} md={4}>
                <Controller
                  name="endingDateTime"
                  control={control}
                  render={({ field: { onChange, value } }) => (
                    <TextField
                      fullWidth
                      label="Conclusion"
                      type="datetime-local"
                      value={value || ""}
                      onChange={handleEndingDatetimeChange(onChange)}
                      error={!!errors.endingDateTime}
                      helperText={errors.endingDateTime?.message}
                      InputLabelProps={{ shrink: true }}
                      variant="outlined"
                      sx={{ "& .MuiOutlinedInput-root": { borderRadius: "12px" } }}
                    />
                  )}
                />
              </Grid>

              <Grid item xs={12} md={4}>
                <Controller
                  name="effectiveEndingDateTime"
                  control={control}
                  render={({ field: { onChange, value } }) => (
                    <TextField
                      fullWidth
                      label="Actual Conclusion"
                      type="datetime-local"
                      value={value || ""}
                      onChange={handleEffectiveEndingDatetimeChange(onChange)}
                      error={!!errors.effectiveEndingDateTime}
                      helperText={errors.effectiveEndingDateTime?.message}
                      InputLabelProps={{ shrink: true }}
                      variant="outlined"
                      sx={{ "& .MuiOutlinedInput-root": { borderRadius: "12px" } }}
                    />
                  )}
                />
              </Grid>
            </Grid>
          </Grid>

          {/* Section 3: Add-Ons */}
          <Grid item xs={12}>
            <Typography variant="h5" fontWeight="700" color={colors.greenAccent[500]} sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
              <Plus size={22} /> Tailored Enhancements
            </Typography>
            <Divider sx={{ mb: 3 }} />
            <Box sx={{ border: `1px solid ${colors.primary[300]}`, borderRadius: '16px', p: 1, bgcolor: theme.palette.mode === "dark" ? "rgba(255,255,255,0.02)" : "rgba(0,0,0,0.01)" }}>
              <AddonsList
                categoryFilters={categoryFilters}
                searchQuery={searchQuery}
                onCategoryFilterChange={handleCategoryFilterChange}
                onSearchQueryChange={handleSearchQueryChange}
                onItemQuantityChange={handleItemQuantityChange}
                requestedAddOns={getValues().addOns ?? []}
                addOnsData={addOnsData.content}
              />
            </Box>
          </Grid>

          {/* Section 4: Financial Summary */}
          <Grid item xs={12}>
            <Typography variant="h5" fontWeight="700" color={colors.greenAccent[500]} sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
              <DollarSign size={22} /> Financial Strategy
            </Typography>
            <Divider sx={{ mb: 3 }} />
            <Grid container spacing={3}>
              <Grid item xs={12} md={8}>
                <Paper sx={{ p: 3, borderRadius: '16px', bgcolor: theme.palette.mode === "dark" ? colors.primary[400] : colors.grey[50] }}>
                  <Typography variant="h6" fontWeight="700" sx={{ mb: 2, color: colors.grey[100] }}>Investment Breakdown</Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={6}><Typography color={colors.grey[300]}>Add-Ons Portfolio:</Typography></Grid>
                    <Grid item xs={6} textAlign="right"><Typography fontWeight="600">{USDollar.format(getValues().addOnsTotalCost ?? 0)}</Typography></Grid>

                    <Grid item xs={6}><Typography color={colors.grey[300]}>Estate Rental:</Typography></Grid>
                    <Grid item xs={6} textAlign="right"><Typography fontWeight="600">{USDollar.format(subTotalBreakDown.facilityRental)}</Typography></Grid>

                    <Grid item xs={6}><Typography color={colors.grey[300]}>Professional Cleaning:</Typography></Grid>
                    <Grid item xs={6} textAlign="right"><Typography fontWeight="600">{USDollar.format(subTotalBreakDown.facilityCleaningFees)}</Typography></Grid>

                    <Grid item xs={6}><Typography color={colors.grey[300]}>Guest Accommodations:</Typography></Grid>
                    <Grid item xs={6} textAlign="right">
                      <Typography fontWeight="600">{USDollar.format(subTotalBreakDown.seats.seatRateTotal)}</Typography>
                      <Typography variant="caption" display="block" color={colors.grey[400]}>
                        {subTotalBreakDown.seats.seatsCount} seats @ {USDollar.format(subTotalBreakDown.seats.seatPrice)}
                      </Typography>
                    </Grid>

                    <Grid item xs={6}><Typography color={colors.grey[300]}>Overtime Reserve:</Typography></Grid>
                    <Grid item xs={6} textAlign="right">
                      <Typography fontWeight="600">{USDollar.format(subTotalBreakDown.overtime.totalCost)}</Typography>
                      <Typography variant="caption" display="block" color={colors.grey[400]}>
                        {subTotalBreakDown.overtime.hours.toFixed(2)} hrs @ {USDollar.format(subTotalBreakDown.overtime.overtimeRate)}/hr
                      </Typography>
                    </Grid>

                    <Grid item xs={12}><Divider sx={{ my: 1 }} /></Grid>

                    <Grid item xs={6}><Typography variant="h6" fontWeight="700">Net Subtotal:</Typography></Grid>
                    <Grid item xs={6} textAlign="right"><Typography variant="h6" fontWeight="700" color={colors.greenAccent[500]}>{USDollar.format(subTotal)}</Typography></Grid>
                  </Grid>
                </Paper>
              </Grid>

              <Grid item xs={12} md={4}>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                  <Paper sx={{ p: 3, borderRadius: '16px', bgcolor: theme.palette.mode === "dark" ? colors.primary[400] : colors.grey[50] }}>
                    <Typography variant="h6" fontWeight="700" sx={{ mb: 2, color: colors.grey[100] }}>Appreciate Value</Typography>
                    <Grid container spacing={2}>
                      <Grid item xs={12}>
                        <FormControl fullWidth>
                          <InputLabel id="discount-type-label" sx={{ color: colors.grey[300], "&.Mui-focused": { color: colors.greenAccent[500] } }}>Benefit Type</InputLabel>
                          <Controller
                            name="discount.type"
                            control={control}
                            render={({ field }) => (
                              <Select
                                labelId="discount-type-label"
                                label="Benefit Type"
                                {...field}
                                sx={{
                                  borderRadius: "12px",
                                  color: colors.grey[100],
                                  "& .MuiOutlinedInput-notchedOutline": { borderColor: colors.grey[400] },
                                  "&.Mui-focused .MuiOutlinedInput-notchedOutline": { borderColor: colors.greenAccent[500] },
                                  "& .MuiSvgIcon-root": { color: colors.grey[100] }
                                }}
                              >
                                <MenuItem value="" sx={{ color: colors.grey[100] }}>None</MenuItem>
                                {Object.values(Constants.DISCOUNT_TYPES).map((type) => (
                                  <MenuItem key={type} value={type} sx={{ color: colors.grey[100] }}>{type}</MenuItem>
                                ))}
                              </Select>
                            )}
                          />
                        </FormControl>
                      </Grid>

                      {discountType && (
                        <Grid item xs={12}>
                          <Controller
                            name={discountType === Constants.DISCOUNT_TYPES.Amount ? "discount.amount" : "discount.percentage"}
                            control={control}
                            render={({ field }) => (
                              <TextField
                                fullWidth
                                label={discountType === Constants.DISCOUNT_TYPES.Amount ? "Credit Amount" : "Percentage Off"}
                                type="number"
                                {...field}
                                onChange={handleDiscountValueChange(field.onChange)}
                                error={!!errors.discount?.amount || !!errors.discount?.percentage}
                                helperText={errors.discount?.amount?.message || errors.discount?.percentage?.message}
                                variant="outlined"
                                sx={{ "& .MuiOutlinedInput-root": { borderRadius: "12px" } }}
                                InputProps={{
                                  startAdornment: (
                                    <InputAdornment position="start">
                                      {discountType === Constants.DISCOUNT_TYPES.Amount ? <DollarSign size={18} /> : "%"}
                                    </InputAdornment>
                                  ),
                                }}
                              />
                            )}
                          />
                        </Grid>
                      )}
                    </Grid>
                  </Paper>

                  <Box sx={{ p: 3, borderRadius: '16px', border: `2px solid ${colors.greenAccent[500]}66`, textAlign: 'center' }}>
                    <Typography color={colors.grey[300]} sx={{ mb: 1 }}>Total Project Estimate</Typography>
                    <Typography variant="h3" fontWeight="900" color={colors.greenAccent[500]}>
                      {USDollar.format(getValues().totalPrice ?? 0)}
                    </Typography>
                    <Typography variant="caption" color={colors.grey[400]}>Incl. {USDollar.format(taxe)} Service Taxes</Typography>
                  </Box>
                </Box>
              </Grid>
            </Grid>
          </Grid>

          {/* Action Buttons */}
          <Grid item xs={12} sx={{ display: 'flex', gap: 2, mt: 4 }}>
            <Button
              fullWidth
              variant="outlined"
              onClick={resetForm}
              sx={{
                py: 1.5,
                borderRadius: "12px",
                textTransform: "none",
                fontWeight: "700",
                borderColor: colors.redAccent[500],
                color: colors.redAccent[500],
                "&:hover": { borderColor: colors.redAccent[600], bgcolor: `${colors.redAccent[500]}0D` }
              }}
            >
              Cancel Entry
            </Button>
            <Button
              fullWidth
              type="submit"
              variant="contained"
              sx={{
                py: 1.5,
                borderRadius: "12px",
                textTransform: "none",
                fontWeight: "700",
                bgcolor: colors.greenAccent[500],
                color: theme.palette.mode === "dark" ? colors.primary[500] : colors.primary[100],
                "&:hover": { bgcolor: colors.greenAccent[400], boxShadow: `0 8px 16px ${colors.greenAccent[500]}33` }
              }}
            >
              {isEditing ? 'Authorize Updates' : 'Confirm Reservation'}
            </Button>
          </Grid>
        </Grid>
      </form>
      <ToastContainer />
    </Box>
  );
};

ReservationForm.propTypes = {
  reservation: PropTypes.object,
  updateReservation: PropTypes.func,
  usersData: PropTypes.array.isRequired,
  toggleModal: PropTypes.func,
  setState: PropTypes.func,
  isEditing: PropTypes.bool,
};

export default ReservationForm;
